import { EntityType } from "@mapmycustomers/shared/enum";

import CardType from "@app/enum/dashboard/CardType";
import { DateRange, DateSubRange } from "@app/enum/dashboard/dateRanges";
import { NextDateSubRange } from "@app/enum/dashboard/nextDateRanges";
import ViewMode from "@app/enum/dashboard/ViewMode";
import SourceType from "@app/enum/SourceType";
import CardConfiguration from "@app/types/dashboard/CardConfiguration";
import ActivitiesLoggedCardConfiguration from "@app/types/dashboard/cards/activitiesLogged/ActivitiesLoggedCardConfiguration";
import ActivitiesOverdueCardConfiguration from "@app/types/dashboard/cards/activitiesOverdue/ActivitiesOverdueCardConfiguration";
import CheckInsCardConfiguration from "@app/types/dashboard/cards/checkIns/CheckInsCardConfiguration";
import ClosedStageDealAmountCardConfiguration from "@app/types/dashboard/cards/closedStageDealAmount/ClosedStageDealAmountCardConfiguration";
import ClosedStageDealCountCardConfiguration from "@app/types/dashboard/cards/closedStageDealCount/ClosedStageDealCountCardConfiguration";
import CustomerFaceTimeCardConfiguration, {
  CustomerFaceTimeAggregationType,
} from "@app/types/dashboard/cards/customerFaceTime/CustomerFaceTimeCardConfiguration";
import MileageTrackingCardConfiguration, {
  MileageTrackingAggregationType,
} from "@app/types/dashboard/cards/mileageTracking/MileageTrackingCardConfiguration";
import NewRecordsCardConfiguration from "@app/types/dashboard/cards/newRecords/NewRecordsCardConfiguration";
import NewRoutesCardConfiguration from "@app/types/dashboard/cards/newRoutes/NewRoutesCardConfiguration";
import NoContactInCardConfiguration from "@app/types/dashboard/cards/noContactIn/NoContactInCardConfiguration";
import NoContactInCountCardConfiguration from "@app/types/dashboard/cards/noContactInCount/NoContactInCountCardConfiguration";
import OpenStageDealAmountCardConfiguration from "@app/types/dashboard/cards/openStageDealAmount/OpenStageDealAmountCardConfiguration";
import OutOfFrequencyCardConfiguration from "@app/types/dashboard/cards/outOfFrequency/OutOfFrequencyCardConfiguration";
import RecordsPastDueCardConfiguration from "@app/types/dashboard/cards/recordsPastDue/RecordsPastDueCardConfiguration";
import UpcomingActivitiesCardConfiguration from "@app/types/dashboard/cards/upcomingActivities/UpcomingActivitiesCardConfiguration";

const defaultCardConfiguration: Record<CardType, CardConfiguration<any, any, any>> = {
  [CardType.ACTIVITIES_LOGGED]: {
    criteria: {
      activityTypeIds: [],
      sources: [],
    },
    dateRange: {
      range: DateRange.WEEK,
      subRange: DateSubRange.LAST,
    },
    preferences: {
      cumulative: false,
      showChart: true,
      showRankPosition: true,
      showTrend: true,
    },
  } as ActivitiesLoggedCardConfiguration,
  [CardType.ACTIVITIES_OVERDUE]: {
    criteria: {
      activityTypeIds: [],
      sources: [],
    },
    dateRange: null,
    preferences: null,
  } as ActivitiesOverdueCardConfiguration,
  [CardType.CHECK_INS]: {
    criteria: {
      activityTypeIds: [],
      includeUnverified: false,
      sources: [SourceType.MOBILE],
    },
    dateRange: {
      range: DateRange.WEEK,
      subRange: DateSubRange.LAST,
    },
    preferences: {
      cumulative: false,
      showChart: true,
      showRankPosition: true,
      showTotal: false,
      showTrend: true,
      showUnverified: false,
      showVerified: true,
      verifiedBreakdown: true,
    },
  } as CheckInsCardConfiguration,
  [CardType.CLOSED_STAGE_DEAL_AMOUNT]: {
    criteria: {
      adjustedAmount: false,
      dealStageIds: [],
    },
    dateRange: {
      range: DateRange.WEEK,
      subRange: DateSubRange.LAST,
    },
    preferences: {
      cumulative: true,
      showChart: true,
      showRankPosition: true,
      showTrend: true,
    },
  } as ClosedStageDealAmountCardConfiguration,
  [CardType.CLOSED_STAGE_DEAL_COUNT]: {
    criteria: {
      dealStageIds: [],
    },
    dateRange: {
      range: DateRange.WEEK,
      subRange: DateSubRange.LAST,
    },
    preferences: {
      cumulative: true,
      showChart: true,
      showRankPosition: true,
      showTrend: true,
    },
  } as ClosedStageDealCountCardConfiguration,
  [CardType.CUSTOMER_FACE_TIME]: {
    criteria: {
      activityTypeIds: [],
      aggregationType: CustomerFaceTimeAggregationType.SUM,
      includeUnverified: false,
      sources: [],
    },
    dateRange: {
      range: DateRange.WEEK,
      subRange: DateSubRange.LAST,
    },
    preferences: {
      showChart: true,
      showRankPosition: true,
      showTotal: false,
      showTrend: true,
      showUnverified: false,
      showVerified: true,
      verifiedBreakdown: true,
    },
  } as CustomerFaceTimeCardConfiguration,
  [CardType.MILEAGE_TRACKING]: {
    criteria: {
      aggregationType: MileageTrackingAggregationType.SUM,
    },
    dateRange: {
      range: DateRange.WEEK,
      subRange: DateSubRange.LAST,
    },
    preferences: {
      cumulative: false,
      showChart: true,
      showTrend: true,
    },
  } as MileageTrackingCardConfiguration,
  [CardType.NEW_RECORDS]: {
    criteria: {
      entityType: EntityType.COMPANY,
      sources: [],
    },
    dateRange: {
      range: DateRange.WEEK,
      subRange: DateSubRange.LAST,
    },
    extraFilters: {},
    preferences: {
      cumulative: false,
      showChart: true,
      showRankPosition: true,
      showTrend: true,
    },
  } as NewRecordsCardConfiguration,
  [CardType.NEW_ROUTES]: {
    criteria: {
      entityTypes: [EntityType.COMPANY_ROUTE],
      sources: [],
    },
    dateRange: {
      range: DateRange.WEEK,
      subRange: DateSubRange.THIS,
    },
    preferences: {
      showChart: true,
      showRankPosition: true,
      showTrend: true,
    },
  } as NewRoutesCardConfiguration,
  [CardType.NO_CONTACT_IN]: {
    criteria: {
      entityTypes: [EntityType.COMPANY, EntityType.PERSON, EntityType.DEAL],
      extraFilters: {},
      frequencyStatus: [],
      sources: [],
    },
    dateRange: {
      days: null,
    },
    preferences: {
      defaultMode: ViewMode.MAP,
    },
  } as NoContactInCardConfiguration,
  [CardType.NO_CONTACT_IN_COUNT]: {
    criteria: {
      entityTypes: [EntityType.COMPANY, EntityType.PERSON, EntityType.DEAL],
      frequencyStatus: [],
      sources: [],
    },
    dateRange: {
      days: null,
    },
    preferences: null,
  } as NoContactInCountCardConfiguration,
  [CardType.OPEN_STAGE_DEAL_AMOUNT]: {
    criteria: {
      dealStageIds: [],
      rottingOptions: [],
    },
    dateRange: {
      days: 30,
    },
    preferences: null,
  } as OpenStageDealAmountCardConfiguration,
  [CardType.OUT_OF_FREQUENCY]: {
    criteria: {
      entityTypes: [EntityType.COMPANY, EntityType.PERSON, EntityType.DEAL],
      sources: [],
    },
    dateRange: null,
    preferences: {
      defaultMode: ViewMode.MAP,
    },
  } as OutOfFrequencyCardConfiguration,
  [CardType.RECORDS_PAST_DUE]: {
    criteria: {
      entityType: EntityType.COMPANY,
      sources: [],
    },
    dateRange: null,
    preferences: null,
  } as RecordsPastDueCardConfiguration,
  [CardType.UPCOMING_ACTIVITIES]: {
    criteria: {
      activityTypeIds: [],
      sources: [],
    },
    dateRange: {
      range: DateRange.WEEK,
      subRange: NextDateSubRange.THIS,
    },
    preferences: { defaultMode: ViewMode.MAP },
  } as UpcomingActivitiesCardConfiguration,
};

export default defaultCardConfiguration;
